import {Button} from '@chakra-ui/react';
import React from 'react';
import {useNavigate} from 'react-router-dom';
import ExternalLink from '../ExternalLink';
import VenueDistanceEnhancer from '../VenueDistanceEnhancer';
import useGetUserLocation from '../../hooks/useUserLocation';
import {parseHtmlWithComponents} from '../../utils/htmlParserCombined';
import {eventKeywordMappings} from '../../utils/eventKeywordMappings';

export interface Place {
  id: number;
  name: string;
  number: number;
  latitude: number;
  longitude: number;
  description?: string;
  actionLink?: string;
  featured?: boolean;
}

interface StyleDefinitions {
  summary?: React.CSSProperties;
  featuredSummary?: React.CSSProperties;
  number?: React.CSSProperties;
  actionButtons?: React.CSSProperties;
  directionsButton?: React.CSSProperties;
  placeButton?: React.CSSProperties;
  nameList?: React.CSSProperties;
}

interface PlaceListingsProps {
  places: Place[];
  styles: StyleDefinitions;
  event?: any; // Event object for nested components
  eventOffers?: any[]; // Event offers for nested components
}

const PlaceListings: React.FC<PlaceListingsProps> = ({places, styles, event, eventOffers}) => {
  if (!places || places.length === 0) return null;
  // Default styles
  const defaultStyles = {
    summary: {
      padding: '15px',
      backgroundImage: 'linear-gradient(to bottom, #f8fbff, #e9f3ff)',
      borderRadius: '8px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '12px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      fontSize: '17px',
      border: '1px solid #cce0ff',
      color: '#0066cc',
    },
    featuredSummary: {
      padding: '15px',
      backgroundImage: 'linear-gradient(135deg, #ffd700, #ffed4e)',
      borderRadius: '8px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '12px',
      boxShadow: '0 4px 8px rgba(255, 215, 0, 0.3)',
      position: 'relative',
      fontSize: '17px',
      border: '2px solid #ffd700',
      color: '#b8860b',
    },
    number: {
      backgroundColor: '#276acd',
      color: 'white',
      fontWeight: 'bold',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: '10px',
    },
    actionButtons: {
      display: 'flex',
      gap: '10px',
      marginBottom: '15px',
      padding: '0 15px',
      justifyContent: 'center',
    },
    directionsButton: {
      padding: '8px 16px',
      backgroundColor: '#0066cc',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      fontWeight: '500',
      fontSize: '13px',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      transition: 'background-color 0.2s',
      minWidth: '150px',
      textDecoration: 'none',
      display: 'inline-block',
      textAlign: 'center',
    },
    placeButton: {
      padding: '8px 16px',
      backgroundColor: '#f8fbff',
      color: '#0066cc',
      border: '1px solid #0066cc',
      borderRadius: '5px',
      fontWeight: '500',
      fontSize: '13px',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      transition: 'background-color 0.2s',
      minWidth: '150px',
      textDecoration: 'none',
      display: 'inline-block',
      textAlign: 'center',
    },
    nameList: {
      fontSize: '16px',
      lineHeight: 1.5,
      marginBottom: '15px',
      paddingLeft: '20px',
      listStyleType: 'none',
    },
  };
  // Merge provided styles with defaults
  const mergedStyles = {
    summary: {...defaultStyles.summary, ...styles.summary},
    featuredSummary: {...defaultStyles.featuredSummary, ...styles.featuredSummary},
    number: {...defaultStyles.number, ...styles.number},
    actionButtons: {...defaultStyles.actionButtons, ...styles.actionButtons},
    directionsButton: {...defaultStyles.directionsButton, ...styles.directionsButton},
    placeButton: {...defaultStyles.placeButton, ...styles.placeButton},
    nameList: {...defaultStyles.nameList, ...styles.nameList},
  };
  // Additional style for descriptions
  const descriptionStyle = {
    fontSize: '15px',
    lineHeight: 1.5,
    marginBottom: '15px',
    marginTop: '10px',
    padding: '0 15px',
    color: '#333',
  };

  const navigate = useNavigate();
  const userLocation = useGetUserLocation();

  // Function to process place descriptions with embedded keywords
  const renderDescription = (description: string) => {
    if (!description) return null;

    // Use parseHtmlWithComponents to process any nested keywords
    return parseHtmlWithComponents(description, {
      keywordMappings: eventKeywordMappings,
      event,
      eventOffers,
    });
  };

  return (
    <>
      <div
        style={{display: 'none'}}
        data-place-listings-map-venues={JSON.stringify(places)}
        data-place-count={places.length}
      />
      {places.map((place) => (
        <details key={place.id}>
          <summary
            style={
              (place.featured
                ? mergedStyles.featuredSummary
                : mergedStyles.summary) as React.CSSProperties
            }>
            <span style={{display: 'flex', alignItems: 'center'}}>
              <span style={mergedStyles.number as React.CSSProperties}>{place.number}</span>
              <span
                dangerouslySetInnerHTML={{
                  __html: typeof place.name === 'string' ? place.name : JSON.stringify(place.name),
                }}
              />
            </span>
          </summary>
          <div style={mergedStyles.actionButtons as React.CSSProperties}>
            <ExternalLink
              style={mergedStyles.directionsButton as React.CSSProperties}
              href={`https://www.google.com/maps/dir/?api=1&destination=${place.latitude},${place.longitude}`}>
              Get Directions
            </ExternalLink>
            <Button
              style={mergedStyles.placeButton as React.CSSProperties}
              onClick={() => {
                navigate(`/place/${place.id}`);
              }}>
              See Place
            </Button>
          </div>

          {/* Render HTML description if available, processing any embedded keywords */}
          {place.description && (
            <div style={descriptionStyle}>
              {typeof place.description === 'string'
                ? renderDescription(place.description)
                : JSON.stringify(place.description)}
            </div>
          )}
        </details>
      ))}
      {userLocation?.data && <VenueDistanceEnhancer />}
    </>
  );
};

export default PlaceListings;
